import { useState, useEffect } from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import useAxiosPrivate from '../../../hooks/useAxiosPrivate';
import MessageBox from '../../MessageBox/MessageBox';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import TextField from '@mui/material/TextField';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import './MemberSegment.css';

export interface IMemberInput {
  firstName: string;
  lastName: string;
  instrument: string;
  microphone: string;
  phone: string;
}

export interface IMemberWrapper {
  id: number;
  inDB: boolean;
  action: 'add' | 'update' | 'delete' | 'none';
  input: IMemberInput;
}

interface props {
  data: IMemberWrapper[];
}

const PHONE_REGEX = /^[\+]?[(]?[0-9]{3}[)]?[-\s\.]?[0-9]{3}[-\s\.]?[0-9]{3,6}$/;

const SUBMIT_BUTTON_TITLE = 'ZAPISZ CZŁONKÓW ZESPOŁU';

const MemberSegment = (props: props) => {
  const { data } = props;
  const axiosPrivate = useAxiosPrivate();
  const starId = sessionStorage.getItem('starId');
  const MEMBER_URL = `/bandMembers/${starId}`; // Użycie starId w URL

  const [memberList, setMemberList] = useState<IMemberWrapper[]>([]);
  const [edit, setEdit] = useState(false);
  const [selected, setSelected] = useState<IMemberWrapper>();
  const [open, setOpen] = useState(false);
  const [result, setResult] = useState(false);

  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
  } = useForm<IMemberInput>({
    defaultValues: {
      firstName: '',
      lastName: '',
      instrument: '',
      microphone: '',
      phone: '',
    },
  });

  useEffect(() => {
    setMemberList(data);
  }, [data]);

  const onSubmit: SubmitHandler<IMemberInput> = (inputData) => {
    if (edit) {
      if (selected) {
        selected.input = inputData;
        selected.action = selected.inDB ? 'update' : 'add';
      }
      setEdit(false);
    } else {
      setMemberList((prev) => [
        ...prev,
        {
          id: 0,
          inDB: false,
          action: 'add',
          input: inputData,
        },
      ]);
    }

    reset();
  };

  const GenerateList = () => (
    <>
      {memberList
        .filter((m) => m.action !== 'delete')
        .map((member) => (
          <ListItem
            key={memberList.indexOf(member)}
            disablePadding
            secondaryAction={
              <IconButton onClick={() => removeMember(member)}>
                <DeleteIcon style={{ color: '#ffffff' }} />
              </IconButton>
            }
          >
            <ListItemButton
              selected={edit && member === selected}
              onClick={() => selectMember(member)}
            >
              <ListItemText
                primary={`${member.input.firstName} ${member.input.lastName}`}
                style={{ color: '#ffffff' }}
              />
            </ListItemButton>
          </ListItem>
        ))}
    </>
  );

  const removeMember = (member: IMemberWrapper) => {
    if (member.inDB) {
      setMemberList((prev) =>
        prev.map((m) => (m !== member ? m : { ...m, action: 'delete' })))
    } else {
      setMemberList((prev) => prev.filter((m) => m !== member));
    }

    if (member === selected) {
      reset();
      setEdit(false);
    }
  };

  const selectMember = (member: IMemberWrapper) => {
    if (edit && member === selected) {
      reset();
      setEdit(false);
      return;
    }

    setValue('firstName', member.input.firstName);
    setValue('lastName', member.input.lastName);
    setValue('instrument', member.input.instrument);
    setValue('microphone', member.input.microphone);
    setValue('phone', member.input.phone);

    setSelected(member);
    setEdit(true);
  };

  const submitList = async () => {
    try {
      await axiosPrivate.post(MEMBER_URL, JSON.stringify(memberList), {
        headers: { 'Content-Type': 'application/json' },
      });

      setResult(true);
    } catch (error) {
      setResult(false);
    }

    setOpen(true);
  };

  return (
    <div className="member-container">
      <h2>Członkowie Zespołu</h2>
      <form className="member-content" onSubmit={handleSubmit(onSubmit)}>
        <Controller
          name="firstName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Imię</label>
              <TextField {...field} error={errors.firstName ? true : false} />
            </div>
          )}
        />
        <Controller
          name="lastName"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Nazwisko</label>
              <TextField {...field} error={errors.lastName ? true : false} />
            </div>
          )}
        />
        <Controller
          name="instrument"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Instrument</label>
              <TextField {...field} error={errors.instrument ? true : false} />
            </div>
          )}
        />
        <Controller
          name="microphone"
          control={control}
          rules={{ required: true }}
          render={({ field }) => (
            <div>
              <label>* Mikrofon / Linia</label>
              <TextField {...field} error={errors.microphone ? true : false} />
            </div>
          )}
        />
        <Controller
          name="phone"
          control={control}
          rules={{ pattern: PHONE_REGEX }}
          render={({ field }) => (
            <div>
              <label>Telefon</label>
              <TextField {...field} error={errors.phone ? true : false} />
            </div>
          )}
        />
        <Button
          variant="contained"
          type="submit"
          sx={{ borderRadius: '0', marginTop: '10px' }}
        >
          {edit ? <EditIcon /> : <PersonAddIcon />}
        </Button>
        <List className="member-list" disablePadding>
          <GenerateList />
        </List>
      </form>
      <Button variant="contained" size="large" onClick={submitList}>
        {SUBMIT_BUTTON_TITLE}
      </Button>
      <MessageBox open={open} setOpen={setOpen} error={!result} />
    </div>
  );
};

export default MemberSegment;
